/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'formik';
import PropTypes from 'prop-types';

import RadioButton from './RadioButton';
import { mediaHelper } from 'utils';
import { FontWeight, Text } from 'components/Text';
import Colors from 'constants/Colors';
import ValidationError from '../ValidationError';

const propTypes = {
  fieldName: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  label: PropTypes.string,
  row: PropTypes.bool,
};

const defaultProps = {
  fieldName: PropTypes.string,
  options: PropTypes.array,
  onChange: "",
  label: null
};

const StyledRadioButton = styled(RadioButton)`
  margin-bottom: 8px;

  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }
  ${mediaHelper.sm`
    margin-bottom: 0;
    margin-right: 24px;
  `}
`;

const RadioButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${({row}) => row ? `flex-direction: row;` : `flex-direction: column;`}
`;

const RadioLabelContainer = styled.div`
  margin-bottom: 16px;
`;

const RadioLabel = styled(Text).attrs({
  weight: FontWeight.SemiBold
})`
  font-size: 14px;
  line-height: 150%;
  color: ${Colors.GRAY[1]};

  ${({ meta }) =>
    meta && meta.touched && meta.error
      ? css`
          color: ${Colors.DANGER};
        `
      : ''}
`;

const RadioButtonGroup = ({
  fieldName,
  options,
  onChange,
  label,
  isRequired,
  hideLabelAsterisk,
  validationMinHeight,
  form: { touched, errors },
  ...props
}) => {
  const meta = {
    touched: touched[fieldName],
    error: errors[fieldName]
  };

  return (
    <>
      {label && (
        <RadioLabelContainer>
          <RadioLabel
            meta={meta}
            isRequired={isRequired}
            weight={FontWeight.SemiBold}
          >
            {isRequired && !hideLabelAsterisk ? '* ' : ''}
            {label}
          </RadioLabel>
        </RadioLabelContainer>
      )}
      <RadioButtonGroupContainer {...props}>
        {options.map((el, index) => (
          <Field
            key={index}
            name={fieldName}
            option={el.value}
            label={el.label}
            component={StyledRadioButton}
            tabIndex={el.tabIndex}
            onChange={onChange}
          />
        ))}
      </RadioButtonGroupContainer>
      <ValidationError meta={meta} validationMinHeight={validationMinHeight} />
    </>
  );
};

RadioButtonGroup.propTypes = propTypes;
RadioButtonGroup.defaultProps = defaultProps;

export default RadioButtonGroup;
